import { isStorageAvailable } from '../../common/utils/isStorageAvailable';
import {
  getCart,
  getCollectionsInCart,
  getDiscountSpendings,
  debouncedSendAddedOffersToBackend,
} from '../api';
import { logInfo, logError } from '../utils/logging';

const ADDED_OFFERS_KEY = 'giftbox-added-offers';
export const SETTINGS_KEY = 'giftbox-settings';
const JUST_ADDED_OFFERS_KEY = 'giftbox-just-added-offers';

export function getProductOriginalPrice(variant) {
  return (
    (variant.compare_at_price > variant.price
      ? variant.compare_at_price
      : variant.price) / 100
  );
}

export async function saveAddedOffer(cartToken, offer) {
  const addedOffers = [
    ...getAddedOffers(),
    {
      offerId: offer.id,
      productId: offer.shopify_product_id || null,
      productVariantId: offer.shopify_product_variant_id,
      productHandle: offer.shopify_product_handle,
      threshold: offer.spend || offer.threshold,
      type: offer.type,
      cartToken: cartToken,
    },
  ];

  localStorage.setItem(ADDED_OFFERS_KEY, JSON.stringify(addedOffers));

  return debouncedSendAddedOffersToBackend(addedOffers, cartToken);
}

export function getAddedOffers() {
  let data = null;
  if (isStorageAvailable()) {
    data = localStorage.getItem(ADDED_OFFERS_KEY);
  }
  if (data) {
    try {
      return JSON.parse(data);
    } catch (error) {
      return [];
    }
  }
  return [];
}

export function getSettings() {
  const data = localStorage.getItem(SETTINGS_KEY);
  if (data) {
    try {
      return JSON.parse(data);
    } catch (error) {
      return {};
    }
  }
  return {};
}

export function saveJustAddedOffers(offerIds) {
  sessionStorage.setItem(JUST_ADDED_OFFERS_KEY, JSON.stringify(offerIds));
}

export function getJustAddedOffers() {
  const data = sessionStorage.getItem(JUST_ADDED_OFFERS_KEY);
  if (data) {
    try {
      sessionStorage.removeItem(JUST_ADDED_OFFERS_KEY);
      return JSON.parse(data);
    } catch (error) {
      return [];
    }
  }
  return [];
}

function getCartPrice(cart) {
  let spending = cart.discountSpendings
    ? cart.discountSpendings.total_spending
    : cart.total_price;
  return spending / 100;
}

function getCartCollectionProductsSpend(offer, cart, collectionsInCart) {
  const collectionProductsInCart =
    collectionsInCart[offer.target_shopify_collection_id] || [];

  let filteredCartItems = cart.items.filter((item) =>
    collectionProductsInCart.includes(item.product_id),
  );

  if (cart.discountSpendings) {
    filteredCartItems = cart.discountSpendings.spendings_per_line_item.filter(
      (spendingItem) =>
        filteredCartItems.find(
          (filteredItem) => filteredItem.id === spendingItem.cart_line_item_id,
        ),
    );
  }

  let cartCollectionProductsSpend = 0;

  for (const item of filteredCartItems) {
    let price = item.final_line_price;
    if (cart.discountSpendings) {
      price -= item.discount_amount;
    }
    cartCollectionProductsSpend += price / 100;
  }
  console.assert(
    cartCollectionProductsSpend >= 0,
    `spend in collection cant be negative, value: ${cartCollectionProductsSpend}`,
  );
  return cartCollectionProductsSpend;
}

function coveredProductsInCart(offer, cart) {
  if (offer.threshold_mode !== 'product_added') {
    return false;
  }
  const items = cart.items.filter(
    (item) => item.product_id === offer.target_shopify_product_id,
  );
  let quantity = 0;
  items.forEach((item) => {
    quantity += item.quantity;
  });
  return quantity >= offer.target_min_quantity;
}

function coveredCollectionsInCart(offer, cart, collectionsInCart) {
  if ('collection_added' !== offer.threshold_mode) {
    return false;
  }

  const collectionProductsInCart =
    collectionsInCart[offer.target_shopify_collection_id] || [];

  const filteredCartItems = cart.items.filter((item) =>
    collectionProductsInCart.includes(item.product_id),
  );
  let cartCollectionProductsQuantity = 0;

  for (const item of filteredCartItems) {
    cartCollectionProductsQuantity += item.quantity;
  }

  return cartCollectionProductsQuantity >= offer.target_min_quantity;
}

function coveredCollectionsInCartWithSpend(offer, cart, collectionsInCart) {
  if ('spend_in_collection' !== offer.threshold_mode) {
    return false;
  }

  return (
    getCartCollectionProductsSpend(offer, cart, collectionsInCart) >=
    offer.threshold
  );
}

function coveredThresholdInCart(offer, cart) {
  if ('free_from' !== offer.threshold_mode) {
    return false;
  }

  const cartTotalPrice = getCartPrice(cart);

  return offer.threshold && cartTotalPrice >= offer.threshold;
}

export function offerOverThreshold(offer, cart, collectionsInCart) {
  return (
    coveredThresholdInCart(offer, cart) ||
    coveredProductsInCart(offer, cart) ||
    coveredCollectionsInCart(offer, cart, collectionsInCart) ||
    coveredCollectionsInCartWithSpend(offer, cart, collectionsInCart)
  );
}

export function remainingOfferAmount(offer, cart, collectionsInCart) {
  const { threshold } = offer;

  if ('spend_in_collection' === offer.threshold_mode) {
    const spendInCollection = getCartCollectionProductsSpend(
      offer,
      cart,
      collectionsInCart,
    );
    return threshold - spendInCollection;
  }

  return threshold - getCartPrice(cart);
}

export function determineEligibleOffers(
  offers,
  cart,
  addedOffers,
  collectionsInCart,
) {
  return offers.filter(
    (offer) =>
      addedOffers.every((p) => p.offerId !== offer.id) &&
      offerOverThreshold(offer, cart, collectionsInCart),
  );
}

export function updateCachedData(cart, offers, collectionsInCart) {
  let addedOffers = getAddedOffers();
  // Remove offers from old sessions
  addedOffers = addedOffers.filter((p) => p.cartToken === cart.token);
  // Remove unpublished offers
  addedOffers = addedOffers.filter((p) =>
    offers.find((o) => o.id === p.offerId),
  );
  // Remove offers that fell below threshold
  addedOffers = addedOffers.filter((addedProduct) =>
    offerOverThreshold(
      offers.find((o) => o.id === addedProduct.offerId),
      cart,
      collectionsInCart,
    ),
  );

  localStorage.setItem(ADDED_OFFERS_KEY, JSON.stringify(addedOffers));
}

function calculateThresholdFromCollection(offer, cart, collectionsInCart) {
  const collectionProductsInCart =
    collectionsInCart[offer.target_shopify_collection_id] || [];

  const filteredCartItems = cart.items.filter((item) =>
    collectionProductsInCart.includes(item.product_id),
  );
  let cartCollectionProductsTotalPrice = 0;
  for (const item of filteredCartItems) {
    cartCollectionProductsTotalPrice +=
      (offer.target_min_quantity * item.price) / 100;
  }

  return cartCollectionProductsTotalPrice;
}

export function isProductUnavailableOrLoading(product) {
  return !product || !product.variants;
}

export function availableProductVariants(product, offer) {
  if (isProductUnavailableOrLoading(product)) return;
  if (
    !offer.shopify_product_variants ||
    offer.shopify_product_variants.length === 0
  ) {
    return product.variants;
  }
  if (offer.isAdminOffer) {
    const products = product.variants.filter(
      (productVariant) =>
        productVariant.available &&
        offer.shopify_product_variants.find(
          (offerVariant) => offerVariant == productVariant.id,
        ),
    );
    return products;
  }
  return product.variants.filter((v) =>
    offer.shopify_product_variants.includes(v.id),
  );
}

export function getFilteredAddedOffers() {
  const settings = getSettings();
  const offers = getAddedOffers();

  if (settings.gift_only_highest_gift) {
    let mostValuableOffer = offers.reduce((max, current) =>
      max.threshold > current.threshold ? max : current,
    );

    // Handling cornercase when offers have same threshold
    const freeShippingOffer = offers.find(
      (o) => o.type === 'FreeShippingOffer',
    );
    mostValuableOffer =
      freeShippingOffer?.threshold === mostValuableOffer.threshold
        ? freeShippingOffer
        : mostValuableOffer;

    if (mostValuableOffer.type === 'FreeShippingOffer') {
      return [];
    }
  }

  return offers.filter((offer) => offer.productVariantId);
}

export async function addEligibleOffersToCart({
  offers,
  products,
  offersCallback,
  settings,
  discountCode,
}) {
  logInfo('addEligibleOffersToCart', {
    offers,
    products,
    offersCallback,
    settings,
    discountCode,
  });
  let cart = await getCart();
  const collection_ids = offers
    .filter((o) =>
      ['collection_added', 'spend_in_collection'].includes(o.threshold_mode),
    )
    .map((o) => o.target_shopify_collection_id);
  const product_ids = cart.items.map((i) => i.product_id);
  const collectionsInCart = await getCollectionsInCart(
    collection_ids,
    product_ids,
  );

  if (
    discountCode &&
    settings.discount_adjust_spending &&
    settings.discounts_enabled
  ) {
    logInfo('addEligibleOffersToCart:before:discount_adjust_spending', cart);
    try {
      if (cart.item_count > 0) {
        const {
          total_spending,
          spendings_per_line_item,
        } = await getDiscountSpendings(discountCode, cart);
        cart = {
          ...cart,
          discountSpendings: { total_spending, spendings_per_line_item },
        };
      }
      logInfo('addEligibleOffersToCart:after:discount_adjust_spending', cart);
    } catch (error) {
      logError('addEligibleOffersToCart:discounted cart error', error);
    }
  }

  updateCachedData(cart, offers, collectionsInCart);

  const addedOffers = getAddedOffers();

  const eligibleOffers = determineEligibleOffers(
    offers,
    cart,
    addedOffers,
    collectionsInCart,
  );

  for (const offer of eligibleOffers) {
    if (offer.threshold_mode === 'product_added') {
      offer.spend =
        cart.items.find(
          (item) => item.product_id === offer.target_shopify_product_id,
        ).price / 100;
    }
    if (
      ['collection_added', 'spend_in_collection'].includes(offer.threshold_mode)
    ) {
      offer.spend = calculateThresholdFromCollection(
        offer,
        cart,
        collectionsInCart,
      );
    }
    if (offer.type === 'ProductOffer') {
      const product = products.find(
        ({ id }) => id === offer.shopify_product_id,
      );
      offer.shopify_product_variant_id = availableProductVariants(
        product,
        offer,
      ).find((v) => v.available)?.id;
    }
    await saveAddedOffer(cart.token, offer);
  }

  if (eligibleOffers.length) {
    saveJustAddedOffers(eligibleOffers.map((offer) => offer.id));
  }
  await offersCallback(cart, collectionsInCart);
}

export async function updateOffer(offer, variant, productHandle = null) {
  let addedOffers = getAddedOffers();
  let isDirty = false;
  let cartToken; // This could be simplified. But we need to verify that server is getting addedOffers with current cart token only.
  let variantToAddId = variant && variant.id;

  for (const addedOffer of addedOffers) {
    if (addedOffer.offerId === offer.id) {
      // Basic dirty check to prevent saving unchanged data
      isDirty = isDirty || addedOffer.productVariantId !== variantToAddId;
      cartToken = addedOffer.cartToken;
      addedOffer.productVariantId = variantToAddId;

      if (variant && offer.threshold_mode === 'product_added') {
        addedOffer.threshold = variant.price / 100;
      }

      addedOffer.productHandle =
        productHandle || (variantToAddId && addedOffer.productHandle);

      if (offer.type === 'MultipleProductOffer') {
        const product = offer.products.find(
          (product) =>
            product.shopify_product_handle === addedOffer.productHandle,
        );
        addedOffer.productId = product.shopify_product_id;
      }
    }
  }

  if (!isDirty) {
    return;
  }

  localStorage.setItem(ADDED_OFFERS_KEY, JSON.stringify(addedOffers));

  return debouncedSendAddedOffersToBackend(addedOffers, cartToken);
}

function verifyAddedVariantExistsInOffer(offer, product, addedOffer) {
  switch (offer.type) {
    case 'ProductOffer':
      return (
        offer.shopify_product_handle === product.handle &&
        product.variants.find(
          (variant) => variant.id === addedOffer.productVariantId,
        )
      );
    case 'MultipleProductOffer':
      return offer.products.find(
        (offerProduct) =>
          offerProduct.shopify_product_handle === product.handle &&
          product.variants.find(
            (variant) => variant.id === addedOffer.productVariantId,
          ),
      );
    case 'ServiceOffer':
    case 'FreeShippingOffer':
      return true;
    default:
      throw new Error('Unknown type');
  }
}

export function resetModifiedOffers(offers, products) {
  const addedOffers = getAddedOffers();
  const addedOffersFiltered = addedOffers.filter((addedOffer) => {
    const offer = offers.find((offer) => offer.id === addedOffer.offerId);
    if (!products.length) return !!offer;
    return (
      offer &&
      products.find((product) =>
        verifyAddedVariantExistsInOffer(offer, product, addedOffer),
      )
    );
  });

  localStorage.setItem(ADDED_OFFERS_KEY, JSON.stringify(addedOffersFiltered));
}
